// May 24, 2026 — Per-silo PWA icon + title.
//
// Keeps the apple-touch-icon, shortcut-icon and apple-mobile-web-app-title
// pointed at the CURRENT silo while the visitor is on a silo route.
//
// Jun 2026 (Randy pt-home-icon-fidelity) — two changes that fixed "my icon
// choice is ignored on iOS":
//   1. Icons are referenced by URL (/api/s/<slug>/icon-180.png), never as a
//      `data:` URI. iOS Safari does not honour data: URIs for
//      apple-touch-icon, so it fell back to the static site logo.
//   2. We MUTATE the single apple-touch-icon tag injected at parse time by
//      public/index.html instead of appending a second one. Two candidates
//      meant iOS picked by declared size, and the site-logo tag (which
//      declared 180x180) beat the per-silo tag (which declared nothing).
//
// The manifest <link> is NOT managed here: iOS only reads it at HTML parse,
// so it's owned by the inline script in index.html plus the one-shot reload
// self-heal in PublicSilo.
import { useEffect } from "react";

// Short content hash so a changed icon busts the OS/browser cache.
function rev(s) {
  let h = 5381;
  for (let i = 0; i < (s || "default").length; i++) {
    h = ((h << 5) + h + (s || "default").charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(36);
}

export default function useSiloManifest(silo) {
  useEffect(() => {
    if (!silo || !silo.slug) return;

    const prevTitle = document.title;
    document.title = silo.name ? `${silo.name} · My Silo` : prevTitle;

    const v = rev(silo.home_icon_data_url);
    const base = `/api/s/${encodeURIComponent(silo.slug)}`;

    const touchIcon = document.querySelector('link[rel="apple-touch-icon"]');
    const prevTouchHref = touchIcon ? touchIcon.getAttribute("href") : null;
    if (touchIcon) {
      touchIcon.setAttribute("sizes", "180x180");
      touchIcon.setAttribute("href", `${base}/icon-180.png?v=${v}`);
    }

    let shortcut = document.querySelector('link[data-silo-managed="shortcut-icon"]');
    if (!shortcut) {
      shortcut = document.createElement("link");
      shortcut.setAttribute("rel", "icon");
      shortcut.setAttribute("type", "image/png");
      shortcut.setAttribute("data-silo-managed", "shortcut-icon");
      document.head.appendChild(shortcut);
    }
    shortcut.setAttribute("href", `${base}/icon-192.png?v=${v}`);

    let appleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (!appleTitle) {
      appleTitle = document.createElement("meta");
      appleTitle.setAttribute("name", "apple-mobile-web-app-title");
      document.head.appendChild(appleTitle);
    }
    const prevAppleTitle = appleTitle.getAttribute("content");
    appleTitle.setAttribute("content", silo.name || "My Silo");

    return () => {
      document.title = prevTitle;
      try {
        if (touchIcon && prevTouchHref) touchIcon.setAttribute("href", prevTouchHref);
        if (shortcut && shortcut.parentNode) shortcut.parentNode.removeChild(shortcut);
        if (prevAppleTitle != null && appleTitle) {
          appleTitle.setAttribute("content", prevAppleTitle);
        }
      } catch { /* ignore */ }
    };
  }, [silo]);
}
